// pages/configuration/SellerInformationPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Chip,
  Dialog, DialogContent, DialogActions, CircularProgress, Alert,
  Tooltip, alpha, Fade, Card, Stack, MenuItem, Select, FormControl,
  InputLabel, FormHelperText, useMediaQuery, useTheme, DialogTitle
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Edit as EditIcon, Delete as DeleteIcon,
  Refresh as RefreshIcon, Store as SellerIcon, Close as CloseIcon,
  Phone as PhoneIcon, Email as EmailIcon, Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { tradesService, SellerInfo, SellerInfoPayload, SellerRank } from '../../services/tradesService';

const SELLER_TYPES = ['Individual', 'Company', 'Retailer', 'Wholesaler'];

const SELLER_TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  Individual: { bg: alpha('#2196F3', 0.1), color: '#2196F3' },
  Company:    { bg: alpha('#9C27B0', 0.1), color: '#9C27B0' },
  Retailer:   { bg: alpha('#FF9800', 0.1), color: '#FF9800' },
  Wholesaler: { bg: alpha('#4CAF50', 0.1), color: '#4CAF50' },
};

const SELLER_RANKS = ['A', 'B', 'C', 'D', 'N'] as const;

const SELLER_RANK_COLORS: Record<string, { bg: string; color: string }> = {
  A: { bg: alpha('#4CAF50', 0.12), color: '#2E7D32' },
  B: { bg: alpha('#2196F3', 0.12), color: '#1565C0' },
  C: { bg: alpha('#FF9800', 0.12), color: '#E65100' },
  D: { bg: alpha('#f44336', 0.12), color: '#C62828' },
  N: { bg: alpha('#9C27B0', 0.12), color: '#6A1B9A' },
};

const usePermissions = () => {
  const { user } = useAuth();
  const perms = user?.role?.permissions?.[0];
  const isDataManager = user?.role?.role_name === 'DATA_MANAGER';
  return {
    canCreate: perms?.create_permission ?? false,
    canUpdate: perms?.update_permission ?? false,
    canDelete: (perms?.delete_permission ?? false) || isDataManager || (user?.is_superuser ?? false),
  };
};

// ── Form Dialog ───────────────────────────────────────────────────────────────
interface FormValues {
  seller_type: string; seller_name: string; email: string; mobile: string;
  alternative_mobile: string; address: string; seller_rank: string;   
}

const SellerFormDialog: React.FC<{
  open: boolean; editing: SellerInfo | null;
  onClose: () => void; onSaved: () => void;
}> = ({ open, editing, onClose, onSaved }) => {
  const [saving, setSaving]     = useState(false);
  const [apiError, setApiError] = useState('');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const EMPTY: FormValues = {
    seller_type: 'Individual', seller_name: '', email: '', mobile: '',
    seller_rank: '', alternative_mobile: '', address: '',
  };
  
  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ defaultValues: EMPTY });
  
  useEffect(() => {
    if (open) {
      setApiError('');
      reset(editing
        ? { seller_type: editing.seller_type, seller_name: editing.seller_name,
            email: editing.email || '', mobile: editing.mobile,
            seller_rank: editing.seller_rank || '',
            alternative_mobile: editing.alternative_mobile || '', address: editing.address || '' }
        : EMPTY
      );
    }
  }, [open, editing, reset]);

  const onSubmit = async (data: FormValues) => {
    setSaving(true); setApiError('');
    try {
      const payload: SellerInfoPayload = {
        seller_type: data.seller_type,
        seller_name: data.seller_name.trim(),
        email: data.email.trim(),
        mobile: data.mobile.trim(),
        seller_rank: data.seller_rank as SellerRank,
        alternative_mobile: data.alternative_mobile.trim(),
        address: data.address.trim(),
      };
      if (editing) await tradesService.updateSeller(editing.seller_info_id, payload);
      else await tradesService.createSeller(payload);
      onSaved(); onClose();
    } catch (e: any) {
      const errs = e?.data?.errors;
      setApiError(
        errs?.seller_name?.[0] || errs?.mobile?.[0] || errs?.seller_rank?.[0] || errs?.email?.[0] ||
        e?.data?.message || 'Something went wrong.'
      );
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
    fullScreen={fullScreen} scroll="paper"
    PaperProps={{ sx: {
      borderRadius: fullScreen ? 0 : 4, overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      maxHeight: fullScreen ? '100%' : 'calc(100% - 64px)',
    } }}>
    <DialogTitle component="div"
      sx={{ flex: '0 0 auto', minHeight: 64, px: { xs: 2, sm: 3 }, py: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SellerIcon sx={{ color: 'white', fontSize: 22 }} />
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
          {editing ? 'Edit Seller' : 'Add New Seller'}
        </Typography>
      </Box>
      <IconButton onClick={onClose} sx={{ color: 'white', p: 0.5 }}><CloseIcon /></IconButton>
    </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 3, flex: 1, overflowY: 'auto' }}>
          {apiError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{apiError}</Alert>}
          <Stack spacing={2.5}>
            <Controller name="seller_type" control={control}
              rules={{ required: 'Seller type is required' }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.seller_type}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Seller Type *</InputLabel>
                  <Select {...field} label="Seller Type *">
                    {SELLER_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                  {errors.seller_type && <FormHelperText>{errors.seller_type.message}</FormHelperText>}
                </FormControl>
              )} />

            <Controller name="seller_name" control={control}
              rules={{ required: 'Seller name is required' }}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Seller Name *" error={!!errors.seller_name}
                  helperText={errors.seller_name?.message}
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon fontSize="small" /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Controller name="mobile" control={control}
              rules={{
                required: 'Mobile number is required',
                pattern: { value: /^[0-9+\-\s()]{7,20}$/, message: 'Enter a valid mobile number' },
              }}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Mobile *" error={!!errors.mobile}
                  helperText={errors.mobile?.message} placeholder="+880 XXXXXXXXX"
                  InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />

            <Controller name="seller_rank" control={control}
              rules={{ required: 'Rank is required' }}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.seller_rank}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Rank *</InputLabel>
                  <Select {...field} label="Rank *">
                    {SELLER_RANKS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                  {errors.seller_rank && <FormHelperText>{errors.seller_rank.message}</FormHelperText>}
                </FormControl>
              )} />

            <Controller name="email" control={control}
              rules={{ pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' } }}
              render={({ field }) => (
                <TextField {...field} fullWidth label="Email" type="email" error={!!errors.email}
                  helperText={errors.email?.message}
                  InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              )} />
              <Controller name="alternative_mobile" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Alternative Mobile" placeholder="+880 XXXXXXXXX"
                    InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon fontSize="small" /></InputAdornment> }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />

              <Controller name="address" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Address" multiline rows={2}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )} />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, gap: 1, flexShrink: 0 }}>
          <Button onClick={onClose} disabled={saving} variant="outlined"
            sx={{ borderRadius: 2, borderColor: alpha('#667eea', 0.4), color: '#667eea' }}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

// ── Delete Dialog ─────────────────────────────────────────────────────────────
const DeleteDialog: React.FC<{
  open: boolean; item: SellerInfo | null; onClose: () => void; onDeleted: () => void;
}> = ({ open, item, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError]       = useState('');

  const handleDelete = async () => {
    if (!item) return;
    setDeleting(true); setError('');
    try { await tradesService.deleteSeller(item.seller_info_id); onDeleted(); onClose(); }
    catch (e: any) { setError(e?.data?.message || 'Failed to delete.'); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ width: 60, height: 60, borderRadius: '50%', background: alpha('#f44336', 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
          <DeleteIcon sx={{ color: '#f44336', fontSize: 28 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} mb={1}>Delete Seller</Typography>
        <Typography variant="body2" color="text.secondary">
          Delete <strong>"{item?.seller_name}"</strong>? This cannot be undone.
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2, textAlign: 'left' }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting} sx={{ borderRadius: 2, minWidth: 100 }}>Cancel</Button>
        <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          sx={{ borderRadius: 2, minWidth: 100 }}>
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const SellerInformationPage: React.FC = () => {
  const { canCreate, canUpdate, canDelete } = usePermissions();

  const [sellers, setSellers]       = useState<SellerInfo[]>([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [filterType, setFilterType] = useState('');

  const [formOpen, setFormOpen]     = useState(false);
  const [editing, setEditing]       = useState<SellerInfo | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting]     = useState<SellerInfo | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (search) params.search = search;
      if (filterType) params.seller_type = filterType;
      const res = await tradesService.listSellers(params);
      setSellers(res.results);
    } catch { } finally { setLoading(false); }
  }, [search, filterType]);

    // Create a helper function to safely format dates
    const formatDateTime = (date: string | Date) => {
      let d: Date;
      
      if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}/)) {
        // Parse DD-MM-YYYY HH:mm:ss format
        const [datePart, timePart] = date.split(' ');
        const [day, month, year] = datePart.split('-');
        const timeString = timePart || '00:00:00';
        // Create date in YYYY-MM-DD format for proper parsing
        d = new Date(`${year}-${month}-${day}T${timeString}`);
      } else {
        d = new Date(date);
      }
      
      const day = String(d.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[d.getMonth()];
      const year = d.getFullYear();
      
      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hoursStr = String(hours).padStart(2, '0');
      
      return `${day}-${month}-${year} ${hoursStr}:${minutes}:${seconds} ${ampm}`;
    };

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Fade in timeout={400}>
      <Box>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Typography variant="h5" fontWeight={800}
              sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Seller Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.25}>Manage seller / supplier records</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} size="small"
                sx={{ border: `1px solid ${alpha('#667eea', 0.3)}`, color: '#667eea' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditing(null); setFormOpen(true); }} size="small"
                sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 12px rgba(102,126,234,0.3)' }}>
                Add Seller
              </Button>
            )}
          </Stack>
        </Box>

        {/* Filters */}
        <Card sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}` }} elevation={0}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
            <TextField size="small" placeholder="Search by name..." value={search}
              onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <Stack direction="row" spacing={1}>
              <Chip label="All" size="small" clickable onClick={() => setFilterType('')}
                sx={{ fontWeight: 600, borderRadius: 1.5,
                  background: filterType === '' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : alpha('#667eea', 0.07),
                  color: filterType === '' ? 'white' : '#667eea' }} />
              {SELLER_TYPES.map(t => (
                <Chip key={t} label={t} size="small" clickable onClick={() => setFilterType(t)}
                  sx={{ fontWeight: 600, borderRadius: 1.5,
                    background: filterType === t
                      ? `linear-gradient(135deg, ${SELLER_TYPE_COLORS[t].color} 0%, ${SELLER_TYPE_COLORS[t].color}99 100%)`
                      : SELLER_TYPE_COLORS[t].bg,
                    color: filterType === t ? 'white' : SELLER_TYPE_COLORS[t].color }} />
              ))}
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {sellers.length} result{sellers.length !== 1 ? 's' : ''}
            </Typography>
          </Stack>
        </Card>

        {/* Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha('#667eea', 0.1)}`, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: alpha('#667eea', 0.04) }}>
                  {['#', 'Seller Name', 'Type', 'Rank', 'Mobile', 'Alternative Mobile', 'Email', 'Address', 'Created By', 'Created At', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.78rem', color: 'text.secondary',
                      py: 1.5, borderBottom: `2px solid ${alpha('#667eea', 0.12)}`, whiteSpace: 'nowrap' }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={32} sx={{ color: '#667eea' }} />
                  </TableCell></TableRow>
                ) : sellers.length === 0 ? (
                  <TableRow><TableCell colSpan={11} align="center" sx={{ py: 5 }}>
                    <SellerIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="body2" color="text.secondary">No sellers found</Typography>
                  </TableCell></TableRow>
                ) : sellers.map((s, idx) => {
                  const tc = SELLER_TYPE_COLORS[s.seller_type] || { bg: alpha('#667eea', 0.1), color: '#667eea' };
                  return (
                    <TableRow key={s.seller_info_id}
                      sx={{ '&:hover': { background: alpha('#667eea', 0.03) }, transition: 'background 0.2s' }}>
                      <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.8rem' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ py: 1.5, fontWeight: 600, fontSize: '0.875rem' }}>{s.seller_name}</TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip size="small" label={s.seller_type}
                          sx={{ background: tc.bg, color: tc.color, fontWeight: 700, height: 22, fontSize: '0.72rem' }} />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {s.seller_rank ? (
                          <Chip size="small" label={s.seller_rank}
                            sx={{ background: SELLER_RANK_COLORS[s.seller_rank]?.bg,
                                  color: SELLER_RANK_COLORS[s.seller_rank]?.color,
                                  fontWeight: 800, height: 22, minWidth: 30, fontSize: '0.75rem' }} />
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                          <Typography variant="body2" fontSize="0.8rem">{s.mobile}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {s.alternative_mobile ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="body2" fontSize="0.8rem">{s.alternative_mobile}</Typography>
                          </Box>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {s.email ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
                            <Typography variant="body2" fontSize="0.8rem">{s.email}</Typography>
                          </Box>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {s.address ? (
                          <Typography variant="body2" fontSize="0.8rem">{s.address}</Typography>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.78rem' }}>{s.created_by}</TableCell>
                      <TableCell sx={{ py: 1.5, color: 'text.secondary', fontSize: '0.78rem' }}>
                         {formatDateTime(s.created_at)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={0.5}>
                          {canUpdate && (
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => { setEditing(s); setFormOpen(true); }}
                                sx={{ color: '#667eea', '&:hover': { background: alpha('#667eea', 0.1) } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {canDelete && (
                            <Tooltip title="Delete">
                              <IconButton size="small" onClick={() => { setDeleting(s); setDeleteOpen(true); }}
                                sx={{ color: '#f44336', '&:hover': { background: alpha('#f44336', 0.1) } }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <SellerFormDialog open={formOpen} editing={editing}
          onClose={() => setFormOpen(false)} onSaved={fetchData} />
        <DeleteDialog open={deleteOpen} item={deleting}
          onClose={() => setDeleteOpen(false)} onDeleted={fetchData} />
      </Box>
    </Fade>
  );
};

export default SellerInformationPage;